import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api, { resolveImage, formatINR, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Package, User, Loader2, ChevronDown } from "lucide-react";

const statusColor = {
  New: "bg-blue-100 text-blue-700",
  Confirmed: "bg-indigo-100 text-indigo-700",
  Processing: "bg-amber-100 text-amber-700",
  Shipped: "bg-purple-100 text-purple-700",
  Delivered: "bg-green-100 text-green-700",
  Cancelled: "bg-red-100 text-red-700",
};

export default function MyAccount() {
  const { user, updateUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "profile";
  const [orders, setOrders] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: user?.name || "", mobile: user?.mobile || "",
    address: user?.addresses?.[0]?.address || "", city: user?.addresses?.[0]?.city || "",
    state: user?.addresses?.[0]?.state || "", pincode: user?.addresses?.[0]?.pincode || "",
  });

  useEffect(() => {
    api.get("/orders").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        name: form.name, mobile: form.mobile,
        address: { address: form.address, city: form.city, state: form.state, pincode: form.pincode },
      });
      updateUser(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 lg:py-14" data-testid="account-page">
      <p className="overline text-gold-dark mb-2">My Account</p>
      <h1 className="font-serif text-4xl lg:text-5xl text-obsidian mb-8">Hi, {user?.name}</h1>

      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-secondary p-1">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-2" /> Profile</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders"><Package className="w-4 h-4 mr-2" /> My Orders</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8">
          <form onSubmit={saveProfile} className="bg-white border border-black/10 p-6 max-w-2xl space-y-5">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Name</Label><Input value={form.name} onChange={set("name")} className="mt-1.5 rounded-sm" data-testid="profile-name" /></div>
              <div><Label>Email</Label><Input value={user?.email} disabled className="mt-1.5 rounded-sm bg-secondary" /></div>
              <div><Label>Mobile</Label><Input value={form.mobile} onChange={set("mobile")} maxLength={10} className="mt-1.5 rounded-sm" data-testid="profile-mobile" /></div>
            </div>
            <div className="border-t border-black/10 pt-5">
              <p className="overline text-gold-dark mb-4">Saved Address</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={set("address")} className="mt-1.5 rounded-sm" data-testid="profile-address" /></div>
                <div><Label>City</Label><Input value={form.city} onChange={set("city")} className="mt-1.5 rounded-sm" data-testid="profile-city" /></div>
                <div><Label>State</Label><Input value={form.state} onChange={set("state")} className="mt-1.5 rounded-sm" data-testid="profile-state" /></div>
                <div><Label>PIN Code</Label><Input value={form.pincode} onChange={set("pincode")} maxLength={6} className="mt-1.5 rounded-sm" data-testid="profile-pincode" /></div>
              </div>
            </div>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-obsidian text-white font-semibold px-7 py-3 hover:bg-gold hover:text-obsidian transition-colors rounded-sm" data-testid="save-profile-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Changes
            </button>
          </form>
        </TabsContent>

        <TabsContent value="orders" className="mt-8">
          {(() => {
            if (orders === null) {
              return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gold" /></div>;
            }
            if (orders.length === 0) {
              return (
                <div className="bg-white border border-black/10 p-12 text-center" data-testid="no-orders">
                  <Package className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="font-serif text-2xl mb-2">No orders yet</p>
                  <p className="text-sm text-muted-foreground mb-6">Your placed orders will appear here.</p>
                  <Link to="/catalogue" className="inline-flex bg-obsidian text-white font-semibold px-6 py-3 hover:bg-gold hover:text-obsidian transition-colors rounded-sm">Shop Now</Link>
                </div>
              );
            }
            return (
              <div className="space-y-4">
                {orders.map((o) => (
                  <div key={o.id} className="bg-white border border-black/10" data-testid={`order-${o.order_number}`}>
                    <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} className="w-full flex items-center justify-between p-5 text-left">
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-semibold text-obsidian">#{o.order_number}</span>
                          <Badge className={`${statusColor[o.status]} border-0 rounded-sm`} data-testid={`order-status-${o.order_number}`}>{o.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })} · {o.items.length} item(s)</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-obsidian">{formatINR(o.total)}</span>
                        <ChevronDown className={`w-4 h-4 transition-transform ${expanded === o.id ? "rotate-180" : ""}`} />
                      </div>
                    </button>
                    {expanded === o.id && (
                      <div className="border-t border-black/10 p-5 space-y-4">
                        {o.items.map((it) => (
                          <div key={it.product_id} className="flex gap-3">
                            <img src={resolveImage(it.image)} alt="" className="w-12 h-14 object-cover bg-secondary" />
                            <div className="flex-1"><p className="text-sm font-medium">{it.name}</p><p className="text-xs text-muted-foreground">Qty {it.quantity} · {formatINR(it.price)}</p></div>
                            <span className="text-sm font-semibold">{formatINR(it.line_total)}</span>
                          </div>
                        ))}
                        <div className="text-xs text-muted-foreground border-t border-black/10 pt-3">
                          {o.shipping_address
                            ? `Ship to: ${o.shipping_address.address}, ${o.shipping_address.city}, ${o.shipping_address.state} - ${o.shipping_address.pincode}`
                            : "Our team will contact you to confirm your delivery address."}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
